
import { Component, computed, inject, input, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IntegrationService } from '@lb/api/mylb/services/integration.service';
import { IIntegrationDetails, IApplicationTypeInfo, IUpdateIntegrationValueRequest } from '@lb/api/mylb/interfaces';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { CopyToClipboardComponent } from '@lb/components/copy-to-clipboard/copy-to-clipboard.component';
import { FormFieldComponent } from '@lb/components/form/form-field/form-field.component';
import {
	MD_DIALOG_REF,
	MDDialogModule,
	MDDialogRef,
	MDDropDownComponent,
	MDIconComponent,
	MDButtonGroupComponent,
	MDButtonToggleComponent,
	IMDDropDownOption
} from '@melodic-ui/components';

export interface IIntegrationDialogData {
	entityGuid: string;
	entityType: number;
	readonly?: boolean;
}

@Component({
	selector: 'lb-integration-dialog',
	standalone: true,
	imports: [
    FormsModule,
    MDDialogModule,
    MDIconComponent,
    MDDropDownComponent,
    MDButtonGroupComponent,
    MDButtonToggleComponent,
    LoaderComponent,
    CopyToClipboardComponent,
    FormFieldComponent
],
	templateUrl: './integration-dialog.component.html',
	styleUrl: './integration-dialog.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class IntegrationDialogComponent implements OnInit {
	private _dialogRef: MDDialogRef = inject(MD_DIALOG_REF);
	private _integrationService = inject(IntegrationService);

	// Inputs
	entityGuid = input.required<string>();
	entityType = input.required<number>();
	readonly = input<boolean>(false);

	// State
	integrationDetails = signal<IIntegrationDetails | null>(null);
	applicationTypes = signal<IApplicationTypeInfo[]>([]);
	isLoading = signal<boolean>(false);
	isSaving = signal<boolean>(false);
	error = signal<string | null>(null);

	// Edit state
	isEditing = signal<boolean>(false);
	showAddForm = signal<boolean>(false);
	editValues = signal<Map<string, string>>(new Map());
	newApplicationType = signal<number | null>(null);
	newApplicationValue = signal<string>('');

	noRemainingApplicationTypes = computed<boolean>(() => this.applicationTypeOptions().length === 0);

	// Computed
	applicationTypeOptions = computed<IMDDropDownOption[]>(() => {
		// Exclude application types already present in integration details
		const existingTypes = new Set<number>(this.integrationDetails()?.applications?.map((a) => a.applicationType) || []);
		return this.applicationTypes()
			.filter((type) => !existingTypes.has(type.applicationID))
			.map((type) => ({
				value: type.applicationID,
				label: type.applicationName
			}));
	});

	newApplicationValueLabel = computed<string>(() => {
		const selected = this.applicationTypes().find((t) => t.applicationID === this.newApplicationType());
		return selected?.columnName ? `${selected.columnName}:` : 'Application Value:';
	});

	canSave = computed(() => {
		return this.newApplicationType() !== null && this.newApplicationValue().trim() !== '';
	});

	onApplicationTypeChange(value: unknown): void {
		this.newApplicationType.set(value as number);
	}

	toggleEditMode(value: unknown): void {
		const mode = Array.isArray(value) ? value[0] : value;
		const isEditing = mode === 'edit';
		this.isEditing.set(isEditing);

		if (!isEditing) {
			// Clear edit state when switching to view mode
			this.showAddForm.set(false);
			this.editValues.set(new Map());
			this.cancelEditing();
		}
	}

	getEditValue(applicationName: string): string {
		const currentEditValues = this.editValues();
		return currentEditValues.get(applicationName) || this.getApplicationValue(applicationName);
	}

	setEditValue(applicationName: string, value: string): void {
		const currentEditValues = this.editValues();
		const newEditValues = new Map(currentEditValues);
		newEditValues.set(applicationName, value);
		this.editValues.set(newEditValues);
	}

	getApplicationValue(applicationName: string): string {
		const app = this.integrationDetails()?.applications?.find((a) => a.applicationName === applicationName);
		return app?.applicationValue || '';
	}

	saveApplicationEdit(applicationName: string): void {
		if (this.isSaving()) return;

		this.isSaving.set(true);
		const newValue = this.getEditValue(applicationName);
		const details = this.integrationDetails();

		if (!details) {
			this.isSaving.set(false);
			return;
		}

		// Find the application info to get the application ID
		const app = details.applications.find((a) => a.applicationName === applicationName);
		if (!app) {
			this.isSaving.set(false);
			return;
		}

		const request: IUpdateIntegrationValueRequest = {
			guid: details.guid,
			entity: this.entityType(),
			application: app.applicationType,
			applicationValue: newValue
		};

		this._integrationService.updateIntegrationValue(request).subscribe({
			next: () => {
				this.isSaving.set(false);
				// Clear the edit value for this application
				const currentEditValues = this.editValues();
				const newEditValues = new Map(currentEditValues);
				newEditValues.delete(applicationName);
				this.editValues.set(newEditValues);
				// Reload data to get updated values
				this.loadData();
			},
			error: (error) => {
				console.error('Error updating integration value:', error);
				this.error.set('Failed to update integration value');
				this.isSaving.set(false);
			}
		});
	}

	cancelApplicationEdit(applicationName: string): void {
		// Clear the edit value for this application
		const currentEditValues = this.editValues();
		const newEditValues = new Map(currentEditValues);
		newEditValues.delete(applicationName);
		this.editValues.set(newEditValues);
	}

	showAddApplicationForm(): void {
		if (this.noRemainingApplicationTypes()) {
			return; // nothing to add
		}
		this.showAddForm.set(true);
		this.newApplicationType.set(null);
		this.newApplicationValue.set('');
	}

	cancelAddForm(): void {
		this.showAddForm.set(false);
		this.newApplicationType.set(null);
		this.newApplicationValue.set('');
	}

	ngOnInit(): void {
		this.loadData();
	}

	loadData(): void {
		this.isLoading.set(true);
		this.error.set(null);

		// Load integration details and entity-specific application types in parallel
		Promise.all([
			this._integrationService
				.getByGuid(this.entityGuid())
				.toPromise()
				.catch((error) => {
					// If entity doesn't exist yet (404), return null so user can add new integrations
					if (error.status === 404) {
						return null;
					}
					throw error; // Re-throw other errors
				}),
			this._integrationService.getApplicationsByEntity(this.entityType()).toPromise()
		])
			.then(([details, appTypes]) => {
				this.integrationDetails.set(details || null);
				this.applicationTypes.set(appTypes || []);
				this.isLoading.set(false);
			})
			.catch((error) => {
				console.error('Error loading integration data:', error);
				this.error.set('Failed to load integration details');
				this.isLoading.set(false);
			});
	}

	startEditing(): void {
		this.isEditing.set(true);
		this.showAddApplicationForm();
	}

	cancelEditing(): void {
		this.isEditing.set(false);
		this.showAddForm.set(false);
		this.editValues.set(new Map());
		this.newApplicationType.set(null);
		this.newApplicationValue.set('');
	}

	saveNewApplication(): void {
		if (!this.canSave()) return;

		this.isSaving.set(true);
		const details = this.integrationDetails();

		const request: IUpdateIntegrationValueRequest = {
			// If no existing details (404), use the entityGuid so a new entry can be created
			guid: details?.guid ?? this.entityGuid(),
			entity: this.entityType(),
			application: this.newApplicationType()!,
			applicationValue: this.newApplicationValue()
		};

		this._integrationService.createIntegrationValue(request).subscribe({
			next: () => {
				this.isSaving.set(false);
				this.cancelAddForm();
				// Reload data to get updated integrations
				this.loadData();
			},
			error: (error) => {
				console.error('Error creating integration value:', error);
				this.error.set('Failed to create integration value');
				this.isSaving.set(false);
			}
		});
	}

	close(): void {
		// Return the latest details so callers can update without refetching
		this._dialogRef.close(this.integrationDetails());
	}

	formatDate(dateString: string): string {
		return new Date(dateString).toLocaleString();
	}

	getApplicationTypeName(applicationTypeID: number): string {
		const appType = this.applicationTypes().find((at) => at.applicationID === applicationTypeID);
		return appType?.applicationName || 'Unknown';
	}
}
